import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { Bell, Check, CheckCheck, ExternalLink } from 'lucide-react'
import { formatMyanmarDateTime } from '@/lib/dateUtils'

interface Notification {
  id: number
  user_id: number
  type: string
  title: string
  message: string
  entity_type: string | null
  entity_id: number | null
  is_read: boolean
  created_by_name: string | null
  created_at: string
}

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatMyanmarDateTime(dateStr)
}

function getNotificationUrl(entityType: string | null, entityId: number | null): string | null {
  if (!entityType || entityId == null) return null
  switch (entityType) {
    case 'service_request': return `/service-requests?open=${entityId}`
    case 'site_survey': return `/site-surveys?open=${entityId}`
    case 'project': return `/customers?open=${entityId}`
    case 'pm_task': return `/project-management`
    case 'weekly_report': return `/weekly-report`
    case 'deal': return `/deals`
    case 'contact': return `/contacts`
    default: return null
  }
}

export default function NotificationPanel() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ['notification-count'],
    queryFn: () => api.get('/notifications/unread-count').then(r => r.data),
    refetchInterval: 30000,
  })

  const { data: notifications } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: () => api.get('/notifications').then(r => r.data),
    enabled: open,
  })

  const markRead = useMutation({
    mutationFn: (id: number) => api.put(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notification-count'] })
    },
  })

  const markAllRead = useMutation({
    mutationFn: () => api.put('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notification-count'] })
    },
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const unread = countData?.count || 0

  const handleNotificationClick = (n: Notification) => {
    if (!n.is_read) markRead.mutate(n.id)
    const url = getNotificationUrl(n.entity_type, n.entity_id)
    if (url) {
      setOpen(false)
      navigate(url)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button onClick={() => { setOpen(!open); if (!open) queryClient.invalidateQueries({ queryKey: ['notifications'] }) }}
        className="relative p-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-[480px] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            {unread > 0 && (
              <button onClick={() => markAllRead.mutate()} className="text-xs text-red-800 hover:text-red-900 flex items-center gap-1">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {!notifications || notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications yet</div>
            ) : (
              notifications.map(n => {
                const hasLink = !!getNotificationUrl(n.entity_type, n.entity_id)
                return (
                  <div key={n.id}
                    className={`px-4 py-3 border-b border-slate-50 transition-colors ${hasLink ? 'hover:bg-slate-50 cursor-pointer' : ''} ${!n.is_read ? 'bg-red-50/50' : ''}`}
                    onClick={() => handleNotificationClick(n)}>
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.is_read ? 'bg-transparent' : 'bg-red-700'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium text-slate-900">{n.title}</span>
                          {hasLink && <ExternalLink size={11} className="text-slate-400 shrink-0" />}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</div>
                        {n.created_by_name && (
                          <div className="text-[10px] text-slate-400 mt-0.5">by {n.created_by_name}</div>
                        )}
                        <div className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
                      </div>
                      {!n.is_read && (
                        <button onClick={e => { e.stopPropagation(); markRead.mutate(n.id) }} className="text-slate-300 hover:text-red-700 shrink-0 mt-1" title="Mark as read">
                          <Check size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
